import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { cn } from "@/lib/utils";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { format } from "date-fns";
import { CURRENCIES, formatDateForStorage, displayDate } from "@/types/ExpenseReport";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import ReceiptUpload from "@/components/ReceiptUpload";

const CreateExpenseReport = () => {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [expenses, setExpenses] = useState<any[]>([]);
  const [newExpense, setNewExpense] = useState({
    description: "",
    amount: "",
    category_id: "",
    date: formatDateForStorage(new Date()),
    currency: "USD", // Add default currency
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isAddExpenseOpen, setIsAddExpenseOpen] = useState(false);
  const [receiptUrls, setReceiptUrls] = useState<{ [key: string]: string }>({});
  const [expenseToEdit, setExpenseToEdit] = useState<any | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [user, setUser] = useState<any>(null);
  
  // Get current user
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
      } else {
        setUser(session.user);
      }
    };
    checkAuth();
  }, [navigate]);

  const { data: currentUser } = useQuery({
    queryKey: ["current-user"],
    queryFn: async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      return user;
    },
  });

  // Get organization data
  const { data: organization } = useQuery({
    queryKey: ["organization", organizationId],
    queryFn: async () => {
      if (!organizationId) return null;
      
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", organizationId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!organizationId,
  });

  const { data: organizations } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("*");
      
      if (error) throw error;
      return data;
    },
  });

  // Get categories
  const { data: categories } = useQuery({
    queryKey: ["categories", organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .eq("organization_id", organizationId);
      
      if (error) throw error;
      return data;
    },
    enabled: !!organizationId,
  });

  const totalAmount = expenses.reduce((sum, exp) => sum + Number(exp.amount), 0);

  const handleAddExpense = () => {
    if (!newExpense.description || !newExpense.amount || !newExpense.category_id) {
      toast({
        title: "Error",
        description: "Please fill in all expense details",
        variant: "destructive",
      });
      return;
    }

    if (isEditMode && expenseToEdit) {
      // Update existing expense
      setExpenses(expenses.map(exp => 
        exp.id === expenseToEdit.id 
          ? { ...newExpense, id: expenseToEdit.id } 
          : exp
      ));
      setIsEditMode(false);
      setExpenseToEdit(null);
    } else {
      // Add new expense
      setExpenses([...expenses, { 
        ...newExpense, 
        id: Date.now(),
        date: newExpense.date // Use the date directly
      }]);
    }
    
    // Reset form and close dialog
    setNewExpense({
      description: "",
      amount: "",
      category_id: "",
      date: formatDateForStorage(new Date()),
      currency: "USD", // Reset currency to default
    });
    setIsAddExpenseOpen(false);
  };

  const handleRemoveExpense = (id: number) => {
    setExpenses(expenses.filter(exp => exp.id !== id));
    // Also remove any receipt URL for this expense
    if (receiptUrls[id]) {
      const newReceiptUrls = { ...receiptUrls };
      delete newReceiptUrls[id];
      setReceiptUrls(newReceiptUrls);
    }
  };

  const handleEditExpense = (expense: any) => {
    setExpenseToEdit(expense);
    setNewExpense({
      description: expense.description,
      amount: expense.amount.toString(),
      category_id: expense.category_id,
      date: expense.date,
      currency: expense.currency,
    });
    setIsEditMode(true);
    setIsAddExpenseOpen(true);
  };

  const handleReceiptUploadComplete = (fileUrl: string, fileName: string, expenseId: string) => {
    setReceiptUrls({
      ...receiptUrls,
      [expenseId]: fileUrl
    });
    toast({
      title: "Receipt uploaded",
      description: `${fileName} has been uploaded successfully`,
    });
  };

  const createExpenseReport = async (status: 'draft' | 'pending') => {
    if (expenses.length === 0) {
      toast({
        title: "Error",
        description: "Please add at least one expense",
        variant: "destructive",
      });
      return null;
    }

    try {
      // Create expense report
      const { data: reportData, error: reportError } = await supabase
        .from("expense_reports")
        .insert({
          organization_id: organizationId,
          generated_by: currentUser?.id,
          total_amount: totalAmount,
          status: status,
          date_from: expenses[0].date,
          date_to: expenses[expenses.length - 1].date,
          currency: expenses[0].currency, // Use the first expense's currency
          report_type: "expense",
          format: "pdf",
        })
        .select()
        .single();

      if (reportError) throw reportError;

      // Create expense report items
      const { error: itemsError } = await supabase
        .from("expense_report_items")
        .insert(
          expenses.map(exp => ({
            expense_report_id: reportData.id,
            description: exp.description,
            amount: exp.amount,
            category_id: exp.category_id,
            date: exp.date,
            organization_id: organizationId,
            created_by: currentUser?.id,
            currency: exp.currency, // Include currency in report items
            receipt_url: receiptUrls[exp.id] || null, // Include receipt URL if available
          }))
        );

      if (itemsError) throw itemsError;
      
      return reportData;
    } catch (error) {
      throw error;
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSaving(true);
      const reportData = await createExpenseReport('pending');
      
      if (reportData) {
        toast({
          title: "Success",
          description: "Expense report submitted successfully",
        });
        navigate(`/app/${organizationId}/expense-reports`);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleSaveAsDraft = async () => {
    try {
      setIsSaving(true);
      const reportData = await createExpenseReport('draft');
      
      if (reportData) {
        toast({
          title: "Success",
          description: "Expense report saved as draft",
        });
        navigate(`/app/${organizationId}/expense-reports`);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={user}
        organization={organization}
        organizations={organizations}
      />
      
      <div className="flex min-h-[calc(100vh-4rem)]">
        <Sidebar className="border-r border-border/50" />
        <main className={cn(
          "flex-1 container mx-auto px-4 py-8 space-y-6",
          "relative z-10 bg-background overflow-y-auto"
        )}>
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold">Create Expense Report</h1>
            <div className="text-lg">
              Total Amount: {CURRENCIES.find(c => c.code === (expenses[0]?.currency || "USD"))?.symbol || "$"}
              {totalAmount.toFixed(2)} {expenses.length > 0 ? expenses[0].currency : "USD"}
            </div>
          </div>

          <div className="flex justify-end mb-4">
            <Dialog open={isAddExpenseOpen} onOpenChange={(open) => {
              setIsAddExpenseOpen(open);
              if (!open) {
                setIsEditMode(false);
                setExpenseToEdit(null);
                setNewExpense({
                  description: "",
                  amount: "",
                  category_id: "",
                  date: formatDateForStorage(new Date()),
                  currency: "USD",
                });
              }
            }}>
              <DialogTrigger asChild>
                <Button className="flex items-center gap-2">
                  <Plus className="h-4 w-4" /> Add Expense
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle>{isEditMode ? "Edit Expense" : "Add New Expense"}</DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="amount">Amount</Label>
                      <Input
                        id="amount"
                        type="number"
                        value={newExpense.amount}
                        onChange={e => setNewExpense({ ...newExpense, amount: e.target.value })}
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="currency">Currency</Label>
                      <Select
                        value={newExpense.currency}
                        onValueChange={value => setNewExpense({ ...newExpense, currency: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map(curr => (
                            <SelectItem key={curr.code} value={curr.code}>
                              {curr.code} ({curr.symbol})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={newExpense.description}
                      onChange={e => setNewExpense({ ...newExpense, description: e.target.value })}
                      placeholder="Expense description"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select
                      value={newExpense.category_id}
                      onValueChange={value => setNewExpense({ ...newExpense, category_id: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories?.map(category => (
                          <SelectItem key={category.id} value={String(category.id)}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date">Date</Label>
                    <Input
                      id="date"
                      type="date"
                      value={newExpense.date}
                      onChange={e => setNewExpense({ ...newExpense, date: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => {
                    setIsAddExpenseOpen(false);
                    setIsEditMode(false);
                    setExpenseToEdit(null);
                  }}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddExpense}>
                    {isEditMode ? "Update Expense" : "Add Expense"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {expenses.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Expenses List</CardTitle>
                <CardDescription>
                  {expenses.length} {expenses.length === 1 ? 'expense' : 'expenses'} added to this report
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto -mx-4 sm:-mx-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="hidden sm:table-cell">Category</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="w-[160px]">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {expenses.map(expense => {
                        // Get the category name for this expense
                        const category = categories?.find(c => c.id === Number(expense.category_id));
                        
                        return (
                          <TableRow key={expense.id}>
                            <TableCell className="font-medium whitespace-nowrap">
                              {displayDate(expense.date)}
                            </TableCell>
                            <TableCell className="max-w-[150px] sm:max-w-none truncate" title={expense.description}>
                              {expense.description}
                            </TableCell>
                            <TableCell className="hidden sm:table-cell">
                              {category?.name}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {CURRENCIES.find(c => c.code === expense.currency)?.symbol}
                              {Number(expense.amount).toFixed(2)} {expense.currency}
                            </TableCell>
                            <TableCell>
                              <div className="flex space-x-1">
                                {receiptUrls[expense.id] ? (
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-8 w-8"
                                    onClick={() => window.open(receiptUrls[expense.id], '_blank')}
                                  >
                                    <img src="/document.svg" alt="View Receipt" className="h-4 w-4" />
                                  </Button>
                                ) : (
                                  <ReceiptUpload
                                    expenseId={expense.id}
                                    organizationId={organizationId || ""}
                                    onUploadComplete={(fileUrl, fileName) => 
                                      handleReceiptUploadComplete(fileUrl, fileName, expense.id)
                                    }
                                  >
                                    <Button variant="outline" size="icon" className="h-8 w-8">
                                      <img src="/upload.svg" alt="Upload Receipt" className="h-4 w-4" />
                                    </Button>
                                  </ReceiptUpload>
                                )}
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() => handleEditExpense(expense)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="icon"
                                  className="h-8 w-8 text-red-500 hover:text-red-700"
                                  onClick={() => handleRemoveExpense(expense.id)}
                                >
                                  <Trash className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground mb-4">No expenses added yet</p>
                <Button 
                  onClick={() => setIsAddExpenseOpen(true)}
                  className="flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" /> Add First Expense
                </Button>
              </CardContent>
            </Card>
          )}

          <div className="flex flex-wrap gap-3 justify-between">
            <Button variant="outline" onClick={() => navigate(`/app/${organizationId}/expense-reports`)}>
              Cancel
            </Button>
            <div className="flex flex-wrap gap-3">
              <Button 
                variant="outline" 
                onClick={handleSaveAsDraft} 
                disabled={expenses.length === 0 || isSaving}
              >
                {isSaving ? "Saving..." : "Save as Draft"}
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={expenses.length === 0 || isSaving}
              >
                {isSaving ? "Submitting..." : "Submit Expense Report"}
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CreateExpenseReport;
